import React, { useEffect, useState } from 'react';
import {
  IDashboardDoughnut,
  IDashboardInvoices,
  IDashboardMembershipInfoForLastThreeYears,
  IStat,
  useDashboardDoughnutQueryQuery,
  useDashboardInvoicesInfoQuery,
  useDashboardMembershipsInfoForTheLastThreeYearsQuery,
  useGetSendGridStatisticsQuery
} from '../../generated/graphql';
import { getMassMailColors, getMembershipColors } from '../../utils/styleUtils';

import PieChart, {
  Export,
  Font,
  Label,
  Legend,
  Margin,
  Series,
  Size,
  Title,
  Tooltip,
  Connector
} from 'devextreme-react/pie-chart';

import {
  Chart,
  Series as ChartSeries,
  CommonSeriesSettings,
  Label as ChartLabel,
  Legend as ChartLegend,
  Export as ChartExport,
  Point,
  ValueAxis
} from 'devextreme-react/chart';
import { Colors, DashboardColors } from '../../utils/theme';
import { Box, Grid } from '@mui/material';

const PALETTE_FOR_MEMBERSHIP_STATISTICS_CHART = [
  DashboardColors.light_blue,
  Colors.main,
  Colors.green
];

const PALETTE_FOR_INVOICE_CHART = [
  Colors.purple,
  Colors.green,
  Colors.third,
  Colors.purple,
  Colors.green,
  Colors.third
];

const Dashboard: React.FunctionComponent = () => {
  const { data } = useDashboardDoughnutQueryQuery();
  const membershipData = useDashboardMembershipsInfoForTheLastThreeYearsQuery();
  const mailStats = useGetSendGridStatisticsQuery();
  const [doughnutData, setDoughnutData] = useState<IDashboardDoughnut[]>([]);
  const [mailData, setMailData] = useState<IStat[]>([]);
  const [membershipsForLastThreeYears, setMembershipsForLastThreeYears] = useState<
    IDashboardMembershipInfoForLastThreeYears[]
  >([]);
  const invoicesData = useDashboardInvoicesInfoQuery();
  const [invoicesPayment, setInvoicesPayment] = useState<IDashboardInvoices[]>([]);

  useEffect(() => {
    if (data && data.result) {
      setDoughnutData(data.result as IDashboardDoughnut[]);
    }
  });

  useEffect(() => {
    if (membershipData.data && membershipData.data.result) {
      setMembershipsForLastThreeYears(
        membershipData.data.result as IDashboardMembershipInfoForLastThreeYears[]
      );
    }
  });

  useEffect(() => {
    if (invoicesData.data && invoicesData.data.result) {
      setInvoicesPayment(invoicesData.data.result as IDashboardInvoices[]);
    }
  });

  useEffect(() => {
    if (mailStats.data && mailStats.data.result) {
      setMailData(mailStats.data.result as IStat[]);
    }
  });

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const doughnutTooltip = (arg: any) => {
    //arg = props from Series
    return {
      text: arg.argument
    };
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const doughnutConnector = (arg: any) => {
    const text = arg.argumentText.trim().split(' ');
    text.pop();
    return `${text.join(' ')} ${(arg.percent * 100).toFixed(2)}%`;
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const membershipStatistics = (arg: any) => {
    return {
      text: `${arg.originalValue} ${arg.seriesName}`
    };
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const invoiceData = (arg: any) => {
    if (arg.seriesName.indexOf('count') > -1) {
      if (arg.seriesName.indexOf('issued') > -1) {
        return {
          text: `${arg.originalValue} issued invoices`
        };
      } else {
        return {
          text: `${arg.originalValue} paid invoices`
        };
      }
    } else {
      return {
        text: `€ ${arg.originalValue}`
      };
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const customizeTooltip = (arg: any) => {
    return {
      text: `${arg.value} ${arg.seriesName}`
    };
  };

  return (
    <Grid container style={{ height: '438px' }}>
      <Grid
        item
        md={4}
        sm={12}
        style={{ marginTop: '31px', boxShadow: `0px 3px 15px ${Colors.lightBlue}` }}
      >
        <div>
          <PieChart
            id="doughnutChart"
            type="doughnut"
            palette={getMembershipColors(doughnutData)}
            dataSource={doughnutData}
          >
            <Size></Size>
            <Margin bottom={0} />
            <Title text="Number Of Members Split By Type">
              <Font color={DashboardColors.title} size="16" />
            </Title>
            <Series argumentField="label" valueField="count">
              <Label visible={true} customizeText={doughnutConnector}>
                <Connector visible={true} />
              </Label>
            </Series>
            <Export enabled={true} />
            <Legend horizontalAlignment="center" verticalAlignment="bottom" />
            <Tooltip enabled={true} customizeTooltip={doughnutTooltip}></Tooltip>
          </PieChart>
        </div>
      </Grid>
      <Grid
        item
        md={8}
        sm={12}
        style={{
          marginTop: '31px',
          paddingLeft: '21px',
          boxShadow: `0px 3px 15px ${Colors.lightBlue}`
        }}
      >
        <div>
          <Chart
            id="membershipChart"
            dataSource={membershipsForLastThreeYears}
            palette={PALETTE_FOR_MEMBERSHIP_STATISTICS_CHART}
          >
            <Margin bottom={0} />
            <Title text="Memberships Statistics L3Y">
              <Font color={DashboardColors.title} size="16" />
            </Title>
            <CommonSeriesSettings type="bar" barPadding={0}>
              <ChartLabel visible={false}></ChartLabel>
            </CommonSeriesSettings>
            <ChartSeries
              argumentField="quarters_date"
              valueField="started_memberships_count"
              name="New"
            ></ChartSeries>
            <ChartSeries
              argumentField="quarters_date"
              valueField="ended_memberships_count"
              name="Ended"
            ></ChartSeries>
            <ChartSeries
              argumentField="quarters_date"
              valueField="renewed_memberships_count"
              name="Renewed"
            ></ChartSeries>
            <ChartExport enabled={true} />
            <ChartLegend
              margin={{ top: 35 }}
              horizontalAlignment="center"
              verticalAlignment="botom"
            />
            <Tooltip enabled={true} customizeTooltip={membershipStatistics}></Tooltip>
          </Chart>
        </div>
      </Grid>
      <Grid
        item
        md={6}
        sm={12}
        style={{
          marginTop: '31px',
          paddingLeft: '21px',
          boxShadow: `0px 3px 15px ${Colors.lightBlue}`
        }}
      >
        <div>
          <Chart id="invoiceChart" dataSource={invoicesPayment} palette={PALETTE_FOR_INVOICE_CHART}>
            <Margin bottom={0} />
            <Title text="Invoice Statistics">
              <Font color={DashboardColors.title} size="16" />
            </Title>
            <CommonSeriesSettings type="bar" barPadding={0} argumentField="date">
              <ChartLabel visible={false}></ChartLabel>
            </CommonSeriesSettings>
            <ChartSeries
              valueField="total_issued_invoices_count"
              name="issued-count"
              axis="number-of-invoices"
              showInLegend={false}
            ></ChartSeries>
            <ChartSeries
              valueField="total_paid_invoices_count"
              name="paid-count"
              axis="number-of-invoices"
              showInLegend={false}
            ></ChartSeries>
            <ChartSeries
              valueField="outstandingг"
              name="outstanding-count"
              axis="number-of-invoices"
              showInLegend={false}
            ></ChartSeries>
            <ChartSeries
              argumentField="date"
              valueField="issued_invoices"
              name="Issued"
              type="spline"
            >
              <Point visible={false} />
            </ChartSeries>
            <ChartSeries argumentField="date" valueField="paid_invoices" name="Paid" type="spline">
              <Point visible={false} />
            </ChartSeries>
            <ChartSeries
              argumentField="date"
              valueField="outstanding"
              name="Outstanding"
              type="spline"
            >
              <Point visible={false} />
            </ChartSeries>
            <ChartExport enabled={true} />
            <ChartLegend
              margin={{ top: 35 }}
              horizontalAlignment="center"
              verticalAlignment="botom"
            />
            <ValueAxis name="sum-in-millions" position="right"></ValueAxis>
            <ValueAxis name="number-of-invoices" position="left"></ValueAxis>
            <Tooltip enabled={true} shared={false} customizeTooltip={invoiceData}></Tooltip>
          </Chart>
        </div>
      </Grid>
      <Grid
        item
        md={6}
        sm={12}
        style={{
          marginTop: '31px',
          paddingLeft: '21px',
          boxShadow: `0px 3px 15px ${Colors.lightBlue}`
        }}
      >
        {/* <div>
          <Chart id="massMailStatistics" dataSource={mailData} palette={MASS_MAIL_STATISTICS_CHART}>
            <Margin bottom={0} />
            <Title text="O365 Statistics">
              <Font color={DashboardColors.title} size="16" />
            </Title>
            <CommonSeriesSettings type="bar" barPadding={0}>
              <ChartLabel visible={false}></ChartLabel>
            </CommonSeriesSettings>
            <ChartSeries argumentField="month" valueField="sent" name="Sent"></ChartSeries>
            <ChartSeries
              argumentField="month"
              valueField="delivered"
              name="Delivered"
            ></ChartSeries>
            <ChartSeries argumentField="month" valueField="read" name="Read"></ChartSeries>
            <ChartExport enabled={true} />
            <ChartLegend
              margin={{ top: 35 }}
              horizontalAlignment="center"
              verticalAlignment="botom"
            />
            <Tooltip enabled={true} customizeTooltip={membershipStatistics}></Tooltip>
          </Chart>
        </div> */}
        <div>
          <Chart
            id="massMailStatistics"
            dataSource={mailData}
            palette={getMassMailColors(mailData)}
          >
            <Title text="Mass Mail Statistics">
              <Font color={DashboardColors.title} size="16" />
            </Title>
            <CommonSeriesSettings argumentField="month" type="stackedBar" />
            <Series valueField="sent" name="Sent" />
            <Series valueField="delivered" name="Delivered" />
            <Series valueField="read" name="Read" />
            <Series valueField="blocks" name="Blocks" />
            <Series valueField="bounces" name="Bounces" />
            <Series valueField="spam_reports" name="Spam Reports" />
            <Legend margin={{ top: 35 }} verticalAlignment="bottom" horizontalAlignment="center" />
            <Tooltip enabled={true} customizeTooltip={customizeTooltip} />
            <Export enabled={true} />
          </Chart>
        </div>
      </Grid>
    </Grid>
  );
};

export default Dashboard;
